import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";

/** One geo-located ZE project on the megamap (mirrors MegamapService row on the backend). */
export interface MegamapProject {
  id: string;
  project_id: string;
  name: string;
  client_name: string | null;
  client_id: string | null;
  status: string;
  is_active: boolean;
  lng: number | null;
  lat: number | null;
  located: boolean;
  route_length_m: number | null;
  parcel_count: number;
  private_unagreed_parcels: number;
  private_owners_pending: number;
  has_wpw_order: boolean;
  wpw_contractor_names: string[];
  needs_subcontractor: boolean;
  total_value: number | null;
  coordinator_ids: string[];
  coordinator_names: string[];
  designer_id: string | null;
  designer_name: string | null;
  branch_id: string | null;
  branch_name: string | null;
  deadline_status: string;
  /** Days until the planned end date (negative = overdue, null = no date). */
  deadline_days: number | null;
}

export interface MegamapTotals {
  route_length_m: number;
  total_value: number;
  parcel_count: number;
  private_unagreed_parcels: number;
  needs_subcontractor: number;
}

export interface MegamapMeta {
  count: number;
  located: number;
  unlocated: number;
  totals: MegamapTotals;
}

export interface MegamapResponse {
  rows: MegamapProject[];
  meta: MegamapMeta;
}

export interface MegamapFilters {
  needs_subcontractor?: boolean;
  has_unagreed_private?: boolean;
  has_wpw_order?: boolean;
  status?: string;
  client?: string;
  coordinator?: string;
  designer?: string;
  branch?: string;
  located?: boolean;
  deadline?: string;
  length_min?: number;
  length_max?: number;
  value_min?: number;
  value_max?: number;
}

function toParams(f: MegamapFilters): Record<string, string> {
  const p: Record<string, string> = {};
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === null || v === "") continue;
    p[k] = typeof v === "boolean" ? String(v) : String(v);
  }
  return p;
}

export async function getMegamap(filters: MegamapFilters = {}): Promise<MegamapResponse> {
  const r = await apiClient.get("/power-projects/megamap/", { params: toParams(filters) });
  return { rows: r.data.data as MegamapProject[], meta: r.data.meta as MegamapMeta };
}

export const useMegamap = (filters: MegamapFilters, enabled = true) =>
  useQuery({
    queryKey: ["pp", "megamap", filters],
    queryFn: () => getMegamap(filters),
    enabled,
  });

/** One hit from the on-map place search: an address (GUGiK), a place (OSM) or a parcel (ULDK). */
export interface MegamapPlace {
  lat: number;
  lng: number;
  label: string;
  /** Context line under the label — "gmina Kołbaskowo, powiat policki" for OSM hits, "" otherwise. */
  detail: string;
  kind: "address" | "place" | "parcel";
  /** OSM classification ("village", "administrative", …) — only on `kind: "place"`. */
  place_type?: string;
  /** [south, north, west, east] when the source knows the feature's extent — lets the map
   * frame a whole village instead of flying to its label point. */
  bbox: [number, number, number, number] | null;
}

/** Below this the search box stays local (projects only) — the backend answers [] anyway,
 * and Nominatim's rate limit is not worth spending on two letters. */
export const PLACE_SEARCH_MIN = 3;

export async function searchPlaces(q: string): Promise<MegamapPlace[]> {
  const r = await apiClient.get("/power-projects/megamap/place-search/", { params: { q } });
  return (r.data.data ?? []) as MegamapPlace[];
}

/** Pass an ALREADY DEBOUNCED query — every distinct value here is one call to an
 * externally rate-limited geocoder. Results are cached by the backend for 12 h. */
export const usePlaceSearch = (q: string, enabled = true) =>
  useQuery({
    queryKey: ["pp", "megamap", "places", q],
    queryFn: () => searchPlaces(q),
    enabled: enabled && q.trim().length >= PLACE_SEARCH_MIN,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

/** UI filter state — the megamap fetches the whole portfolio once and filters in the browser,
 * so the dimension option lists stay stable and filtering is instant. */
/** Deadline buckets: "" = all, "overdue" = po terminie, "3m"/"6m"/"9m"/"12m" = due within N months. */
export type DeadlineBucket = "" | "overdue" | "3m" | "6m" | "9m" | "12m";

export const DEADLINE_BUCKETS: { value: DeadlineBucket; label: string }[] = [
  { value: "", label: "Wszystko" },
  { value: "overdue", label: "Po terminie" },
  { value: "3m", label: "Do 3 mies." },
  { value: "6m", label: "Do 6 mies." },
  { value: "9m", label: "Do 9 mies." },
  { value: "12m", label: "Do 12 mies." },
];

const DEADLINE_CAP_DAYS: Record<string, number> = { "3m": 90, "6m": 180, "9m": 270, "12m": 360 };

export interface UiFilters {
  needsSubcontractor: boolean;
  hasUnagreedPrivate: boolean;
  wpw: "all" | "with" | "without";
  status: string;
  clients: string[]; // client ids
  coordinators: string[]; // coordinator ids; may include the sentinel "unassigned"
  branches: string[]; // branch ids
  deadline: DeadlineBucket;
  /** Project ids explicitly hidden. Empty = all projects shown (all checkboxes ticked). */
  projectsExcluded: string[];
}

export const EMPTY_UI_FILTERS: UiFilters = {
  needsSubcontractor: false,
  hasUnagreedPrivate: false,
  wpw: "all",
  status: "",
  clients: [],
  coordinators: [],
  branches: [],
  deadline: "",
  projectsExcluded: [],
};

export function filterRows(rows: MegamapProject[], f: UiFilters): MegamapProject[] {
  const excluded = f.projectsExcluded.length ? new Set(f.projectsExcluded) : null;
  return rows.filter((p) => {
    if (excluded?.has(p.id)) return false;
    if (f.needsSubcontractor && !p.needs_subcontractor) return false;
    if (f.hasUnagreedPrivate && p.private_unagreed_parcels === 0) return false;
    if (f.wpw === "with" && !p.has_wpw_order) return false;
    if (f.wpw === "without" && p.has_wpw_order) return false;
    if (f.status && p.status !== f.status) return false;
    if (f.clients.length && (!p.client_id || !f.clients.includes(p.client_id))) return false;

    if (f.deadline) {
      const d = p.deadline_days;
      if (f.deadline === "overdue") {
        if (d == null || d >= 0) return false;
      } else {
        const cap = DEADLINE_CAP_DAYS[f.deadline];
        if (d == null || d < 0 || d > cap) return false;
      }
    }

    if (f.branches.length && (!p.branch_id || !f.branches.includes(p.branch_id))) return false;

    if (f.coordinators.length) {
      const wantUnassigned = f.coordinators.includes("unassigned");
      const ids = f.coordinators.filter((x) => x !== "unassigned");
      const matchUnassigned = wantUnassigned && p.coordinator_ids.length === 0;
      const matchId = ids.some((id) => p.coordinator_ids.includes(id));
      if (!matchUnassigned && !matchId) return false;
    }
    return true;
  });
}
